import { useNavigation } from '@react-navigation/native';
import differenceInMonths from 'date-fns/differenceInMonths';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import { LinearGradient } from 'expo-linear-gradient';
import hexToRgba from 'hex-to-rgba';
import groupBy from 'lodash/groupBy';
import sum from 'lodash/sum';
import { Fragment, useState } from 'react';
import { Pressable, StyleSheet } from 'react-native';
import { BarChart, BarChartPropsType, LineChart } from 'react-native-gifted-charts';

import { Icon } from '@oui/app-core/src/components/Icon';
import { PillGroup } from '@oui/app-core/src/components/PillGroup';
import { Label, Text, ThemeText } from '@oui/app-core/src/components/Text';
import { View } from '@oui/app-core/src/components/View';
import { useI18n } from '@oui/app-core/src/lib/i18n';
import { useTheme } from '@oui/app-core/src/styles';
import { parseGQLDate, parseGQLDateTime } from '@oui/lib/src/gqlDate';
import { FragmentOf, graphql, readFragment, ResultOf } from '@oui/lib/src/graphql/tada';
import { GQLDateTime } from '@oui/lib/src/types/scalars';

const Y_AXIS_WIDTH = 35;

export const WordPairingOverviewFragment = graphql(`
  fragment WordPairingOverview on WordPairingWidget {
    wordPairings(input: {}) {
      practiceID
      practiceValues {
        date
      }
      wordPairing {
        timeToCompleteDecreased
        isComplete
        rounds {
          numAnswers
          numCorrectAnswers
          time
        }
      }
    }
  }
`);

function TableLabel({ text }: { text: string }) {
  const { theme } = useTheme();
  return <Text text={text} size={15} color={theme.color.gray300} weight="semibold" />;
}

function TableDivider() {
  const { theme } = useTheme();
  return <View style={{ height: 1, width: '100%', backgroundColor: theme.color.gray600 }} />;
}

function TableRow({
  practice,
}: {
  practice: NonNullable<ResultOf<typeof WordPairingOverviewFragment>>['wordPairings'][number];
}) {
  const { theme } = useTheme();
  const { formatDate, formatNumber } = useI18n();
  const { navigate } = useNavigation();
  return (
    <Pressable
      onPress={() => navigate('WordPairingShow', { practiceID: practice.practiceID })}
      style={({ pressed }) => ({ paddingVertical: 10, opacity: pressed ? 0.3 : 1 })}
    >
      <View row>
        <View style={{ flex: 1, flexBasis: 0 }}>
          <Text
            text={formatDate(parseGQLDate(practice.practiceValues.date), {
              month: 'short',
              day: 'numeric',
            })}
            weight="semibold"
            size={15}
          />
        </View>
        <View row style={{ flex: 1, flexBasis: 0 }}>
          <Text
            text={formatNumber(
              sum(practice.wordPairing.rounds.map((r) => r.numCorrectAnswers)) /
                sum(practice.wordPairing.rounds.map((r) => r.numAnswers)),
              { maximumFractionDigits: 1, style: 'percent' },
            )}
            size={15}
          />
        </View>
        <View row style={{ flex: 1, flexBasis: 0 }}>
          <Text
            text={formatNumber(
              sum(practice.wordPairing.rounds.map((r) => r.time)) /
                sum(practice.wordPairing.rounds.map((r) => r.numAnswers)) /
                1000,
              { maximumFractionDigits: 1, style: 'unit', unit: 'second', unitDisplay: 'short' },
            )}
            size={15}
          />
        </View>
        <Icon name="caret-right" color={theme.color.primary100} size={16} />
      </View>
    </Pressable>
  );
}

function Legend(props: { data: Array<{ color: string; text: string }> }) {
  return (
    <View
      style={{
        gap: 6,
        flexDirection: 'row',
        paddingHorizontal: 50,
        flexWrap: 'wrap',
        justifyContent: 'center',
      }}
    >
      {props.data?.map((d) => {
        return (
          <View row spacing={8} key={d.color}>
            <View style={{ width: 15, height: 15, borderRadius: 3, backgroundColor: d.color }} />
            <Text size={13} text={d.text} />
          </View>
        );
      })}
    </View>
  );
}

function WordPairingOverviewTable({
  data: _data,
}: {
  data: FragmentOf<typeof WordPairingOverviewFragment>;
}) {
  const data = readFragment(WordPairingOverviewFragment, _data);
  return (
    <View>
      <View style={{ flexDirection: 'row', alignItems: 'flex-end', paddingBottom: 10 }}>
        <View style={{ flex: 1, flexBasis: 0 }}>
          <TableLabel text="Date" />
        </View>
        <View style={{ flex: 1, flexBasis: 0 }}>
          <TableLabel text="Avg Score" />
        </View>
        <View style={{ flex: 1, flexBasis: 0 }}>
          <TableLabel text="Avg Time" />
        </View>
        <View style={{ flexBasis: 16 }} />
      </View>
      <TableDivider />
      {data.wordPairings
        .filter((p) => p.wordPairing.rounds.length)
        .map((exercise) => (
          <Fragment key={exercise.practiceID}>
            <TableRow practice={exercise} />
            <TableDivider />
          </Fragment>
        ))}
    </View>
  );
}

const RECENT_ITEMS = 5;

function WordPairingOverviewGraph({
  data: _data,
}: {
  data: FragmentOf<typeof WordPairingOverviewFragment>;
}) {
  const { formatDate } = useI18n();
  const { theme } = useTheme();
  const scoreColor = theme.color.accent100;
  const timeColor = theme.color.accentTwo100;
  const data = readFragment(WordPairingOverviewFragment, _data);
  const [tab, setTab] = useState<'recent' | 'all'>('recent');
  const [width, setWidth] = useState<number>();

  const baseData = (data.wordPairings ?? []).filter((e) => e.wordPairing.isComplete);

  if (!baseData.length) return null;

  const recentData = baseData.slice(0, RECENT_ITEMS).flatMap((d) => {
    const totalCorrect = sum(d.wordPairing.rounds.map((r) => r.numCorrectAnswers ?? 0));

    const totalAnswers = sum(d.wordPairing.rounds.map((r) => r.numAnswers ?? 0));
    const totalTimeMs = sum(d.wordPairing.rounds.map((r) => r.time));

    return [
      {
        value: (totalCorrect / totalAnswers) * 100,
        frontColor: scoreColor,
        spacing: 1,
      },
      {
        value: totalTimeMs / totalAnswers / 1000,
        frontColor: timeColor,
        isSecondary: true,
        spacing: 20,
      },
    ] satisfies NonNullable<BarChartPropsType['data']>[number][];
  });

  const labels = baseData
    .slice(0, RECENT_ITEMS)
    .flatMap((v) => [
      formatDate(parseGQLDate(v.practiceValues.date), { month: 'numeric', day: 'numeric' }),
      '',
    ]);

  const numMonths = Math.abs(
    differenceInMonths(
      parseGQLDate(baseData[0].practiceValues.date),
      parseGQLDate(baseData[baseData.length - 1].practiceValues.date),
    ),
  );
  const allData = Object.entries(
    groupBy(baseData, (p) =>
      numMonths > 1
        ? startOfMonth(parseGQLDate(p.practiceValues.date)).toISOString()
        : startOfWeek(parseGQLDate(p.practiceValues.date)).toISOString(),
    ),
  )
    .sort((a, b) => {
      return a[0] < b[0] ? -1 : 1;
    })
    .map(([key, entries]) => {
      const entryDate = parseGQLDateTime(key as GQLDateTime);
      const labelDate =
        numMonths > 1
          ? formatDate(startOfMonth(entryDate), { month: 'short' })
          : formatDate(startOfWeek(entryDate), { month: 'numeric', day: 'numeric' });

      const totalCorrect = sum(
        entries.flatMap((d) => d.wordPairing.rounds.map((r) => r.numCorrectAnswers ?? 0)),
      );
      const totalAnswers = sum(
        entries.flatMap((d) => d.wordPairing.rounds.map((r) => r.numAnswers ?? 0)),
      );
      const totalTimeMs = sum(entries.flatMap((d) => d.wordPairing.rounds.map((r) => r.time)));

      return {
        value: (totalCorrect / totalAnswers) * 100,
        secondaryValue: totalTimeMs / totalAnswers / 1000,
        labelComponent: () => (
          <ThemeText
            fontTheme={theme.typography.small}
            weight="normal"
            text={labelDate}
            textAlign="center"
          />
        ),
      };
    });

  return (
    <View style={{ gap: 15, paddingVertical: 15 }}>
      <PillGroup
        style={{ paddingHorizontal: 60 }}
        aria-label="Graph time scale"
        value={tab}
        onChangeValue={setTab}
        items={[
          { label: 'Recent', value: 'recent' },
          { label: 'All', value: 'all' },
        ]}
      />
      <View
        style={{
          paddingHorizontal: 20,
          gap: 10,
          width: '100%',
          overflow: 'hidden',
        }}
        onLayout={(e) => {
          if (width !== e.nativeEvent.layout.width) {
            setWidth(e.nativeEvent.layout.width - 40);
          }
        }}
      >
        {tab === 'recent' ? (
          <>
            <View row style={{ justifyContent: 'space-between' }}>
              <Label small text="Accuracy (%)" />
              <Label small text="Time (s)" />
            </View>
            <BarChart
              maxValue={100}
              barWidth={18}
              data={recentData}
              yAxisLabelWidth={Y_AXIS_WIDTH}
              secondaryYAxis={{
                showFractionalValues: true,
                formatYLabel(label) {
                  return Number(label).toFixed(1).toString();
                },
                yAxisLabelWidth: Y_AXIS_WIDTH,
              }}
              disablePress
              disableScroll
              spacing={0}
              initialSpacing={8 / 2}
              endSpacing={8 / 2}
              labelWidth={18 * 2 + 1 * 1} // width of bars + spacing b/t bars in group
              adjustToWidth
              width={typeof width === 'number' ? width - Y_AXIS_WIDTH * 2 : undefined}
              xAxisLabelTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
              xAxisLabelTexts={labels}
              yAxisTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
            />
            <Label small text="Dates" textAlign="center" />
            <View>
              <Legend
                data={[
                  { color: scoreColor, text: 'Avg score' },
                  { color: timeColor, text: 'Avg time' },
                ]}
              />
            </View>
          </>
        ) : (
          <>
            <View row style={{ justifyContent: 'space-between' }}>
              <Label small text="Avg accuracy (%)" />
              <Label small text="Avg time (s)" />
            </View>
            <LineChart
              maxValue={100}
              data={allData}
              secondaryData={allData.map((d) => ({ value: d.secondaryValue }))}
              yAxisLabelWidth={Y_AXIS_WIDTH}
              secondaryYAxis={{
                showFractionalValues: true,
                formatYLabel(label) {
                  return Number(label).toFixed(1).toString();
                },
                yAxisLabelWidth: Y_AXIS_WIDTH,
              }}
              color={scoreColor}
              dataPointsColor={scoreColor}
              secondaryLineConfig={{ color: timeColor, dataPointsColor: timeColor }}
              initialSpacing={8}
              endSpacing={8}
              dataPointsRadius={5}
              disableScroll
              xAxisLabelTextStyle={{ fontFamily: 'OpenSansRegular', ...theme.typography.small }}
              xAxisLabelTexts={labels}
              width={typeof width === 'number' ? width - Y_AXIS_WIDTH * 2 : undefined}
              adjustToWidth
            />
            <Label small text="Dates" textAlign="center" />
          </>
        )}
      </View>
    </View>
  );
}

/**
 * Renders an overview and list of all stress breathing exercise data.
 */
export const WordPairingOverview = ({
  data: _data,
}: {
  data: FragmentOf<typeof WordPairingOverviewFragment>;
}) => {
  return (
    <View testID="WordPairingOverview">
      <View style={{ paddingBottom: 30 }}>
        <LinearGradient
          colors={[hexToRgba('#F7D797', 0), hexToRgba('#F7D797', 0.3)]}
          style={StyleSheet.absoluteFillObject}
          start={[0, 0]}
          end={[0, 1]}
        />
        <WordPairingOverviewGraph data={_data} />
      </View>
      <View
        style={{
          marginTop: -30,
          paddingHorizontal: 22,
          paddingTop: 20,
          borderTopEndRadius: 20,
          borderTopStartRadius: 20,
          backgroundColor: 'white',
        }}
      >
        <WordPairingOverviewTable data={_data} />
      </View>
    </View>
  );
};
